export enum ModuleId {
  PX = 1,
  Game = 2,
  Rent = 3,
  Sale = 4,
  Unused = 5,
  GameRent = 6,
  Banner = 7,
  Statistics = 8,
  Snus = 9,
  CompuShop = 10,
  Golf = 11,
  Access = 12,
  AIM = 13,
  InstantInventory = 14,
  NeoCaddie = 15,
  PayaP = 16,
  Robotics = 17,
}

export interface EndPoint {
  ID: number;
  PartnerID: number;
  Title: string;
  ShortName: string;
  ModuleID: number;
  CustomerID: number;
}

export interface Session {
  Session: string;
  Ticket: string;
  UID: number;
  ForceChange: boolean;
  Blocked: boolean;
  SuperAdmin: boolean;
  SuperUserTools?: boolean;
  ActiveEndPointID: number;
  ModuleID: number;
  PartnerID: number;
  Version: number;
  ClientVersion: string;
}

// ── Impersonation ──────────────────────────────────────

/** Independent of ImpersonationState. SA-only override of perceived
 *  browser environment for date/number rendering and timezone-derived
 *  API params. In-memory only; matches impersonation lifecycle. */
export interface ClientOverride {
  /** IANA tz name, e.g. "Europe/Stockholm". Empty/undefined → browser default. */
  timeZone?: string;
  /** BCP 47 locale, e.g. "sv-SE". Empty/undefined → browser default. */
  locale?: string;
}

/** Mutually exclusive impersonation modes. */
export type ImpersonationState =
  | { mode: "user"; impersonateUID: number; impersonateUserName: string }
  | {
      mode: "select";
      overrideOperatorID?: number;
      overrideSuperAdmin?: boolean;
      overrideIngressGroups?: number[];
      overrideUserGroups?: number[];
      /** DEBUG-only: pretend requests come from this IP (IPv4/IPv6). Backend honours only when real super-admin AND debug build. */
      overrideIPAddress?: string;
    };

/** Data returned by WebGetImpersonateToolData */
export interface ImpersonateToolData {
  Users: ImpersonateToolUser[];
  Operators: ImpersonateToolOperator[];
  UserGroups: ImpersonateToolGroup[];
  IngressGroups: ImpersonateToolGroup[];
}

export interface ImpersonateToolUser {
  UserID: number;
  UserName: string;
  SuperAdmin: boolean;
}

export interface ImpersonateToolOperator {
  ID: number;
  Name: string;
}

export interface ImpersonateToolGroup {
  ID: number;
  Name: string;
}

// PageInfo nested types (backend: PageData class)

export interface WebAdminPage {
  RowID: number;
  Name: string;
  DisplayName: string;
  Type: string;
  Enabled: boolean;
  Available: boolean;
  Modules?: string;
}

export interface WebAdminPageMenuItem {
  ID: number;
  RowID: number;
  ParentID: number;
  Order: number;
}

export interface WebAdminPageData {
  ID: number;
  RowID: number;
  Priority: number;
  TargetDatabase?: string;
  Params?: string[];
  Columns?: string[];
  KeyColumns?: string[];
  FooterColumns?: string[];
  HiddenColumns?: string[];
  HiddenFilters?: string[];
  DefaultOrder?: OrderByParam;
  InfoURL?: string;
  MigratedFilters?: Record<string, string | null>;
  AsOfGetType?: string;
  AsOfCommand?: string;
  AutoUpdateRate?: number | null;
  ClientOptions?: string[];
  RowCountColumn?: string;
  /** When true, cell edits stage locally — a grid-rendered Save button column
   *  (COMPONENT_GENERALSAVE) commits the row. No auto-UpdateGridCell on edit. */
  SaveButton?: boolean;
  /** When true, the grid renders a Delete button column (COMPONENT_GENERALDELETE). */
  DeleteButton?: boolean;
  /** When true, the grid is refetched after each successful UpdateGridCell. */
  RefreshOnUpdate?: boolean;
  /** When true, column headers are not sortable (ClientOptions `nosort`). */
  NoSort?: boolean;
  /** Sparse per-column display/filter/link overrides — JSON object keyed by column
   *  name, each value a subset of ColumnDescription fields. Overrides the global
   *  ColumnInfo (S_Admin_Column_Desc) for this page; a pivot's overrides win further. */
  ColumnDescJson?: string;
}

export interface OrderByParam {
  sColumnName: string;
  OrderType: number;
}

export interface WebAdminPageDataPivot {
  ID: number;
  RowID: number;
  PivotView: string;
  PivotColumn: string;
  PivotValueColumn: string;
  PivotHeaderColumn: string;
  /** Sparse per-column overrides (highest precedence) — same shape as
   *  WebAdminPageData.ColumnDescJson. */
  ColumnDescJson?: string;
}

export interface WebAdminPageHierarchy {
  ID: number;
  RowID: number;
  ParentID: number;
  AvailableHelper: boolean;
}

export interface WebAdminPageInfo {
  ID: number;
  RowID: number;
  Info: string;
  Header: string;
  Order?: number | null;
}

export interface PageData {
  Page: WebAdminPage[];
  MenuItem: WebAdminPageMenuItem[];
  Data: WebAdminPageData[];
  Pivot: WebAdminPageDataPivot[];
  Hierarchy: WebAdminPageHierarchy[];
  PageInfo: WebAdminPageInfo[];
}

// Typed cache item interfaces (matching backend shapes)

export interface KioskCacheItem {
  KioskID: number;
  LocationID: number;
  StoreName: string;
  OwnerOperatorID: number;
  Address: string;
  City: string;
  District: string;
  SerialNumber?: string;
  IpAddress?: string;
  ExternalReference?: string;
  Type?: string;
  LiveStatus?: number;
  LastActivity?: string;
  /** Non-empty marks the kiosk as a test rig (excluded from production lists). */
  TestString?: string;
  /** Lifecycle status string ("Live", "Inactive", "Test", "Transferred",
   *  "Warehouse", "Delivered", "Ordered") sourced from S_Admin_CB_Values. Used
   *  by the kiosk + location filters to rank result lists with live rigs at
   *  the top. */
  InHouseStatus?: string;
}

export interface LocationCacheItem {
  LocationID: number;
  StoreName: string;
  /** Location-level external reference (column sStoreNumber). The Location
   *  equivalent of Kiosk.ExternalReference — surfaced in filter search/display. */
  StoreNumber?: string;
  OwnerOperatorID: number;
  Address: string;
  City: string;
  District: string;
  ZipCode?: string;
  County?: string;
  Suburb?: string;
  Chain?: string;
  ShortName?: string;
  Country?: string;
  Latitude?: string;
  Longitude?: string;
  AccessLevelID: number;
  SegmentID: number;
  /** Windows-style zone name from S_Admin_Location_TimeZone_View, e.g. "Central
   *  European Standard Time". Not usable with Intl — see `TimeZoneIana`. */
  TimeZone?: string;
  /** `TimeZone` resolved to an IANA id server-side ("Europe/Oslo"), or "" when the
   *  name is unset or unmappable. This is what Kiosk* grid cells convert with:
   *  kiosk → LocationID → location → zone. */
  TimeZoneIana?: string;
}

/** One row of the S_Admin_TimeZones cache. There is a row per DST period per zone
 *  name, which is how the legacy client picked an abbreviation; WebAdminCC uses
 *  Intl for the offset and only needs the abbreviation pair. */
export interface TimeZoneCacheItem {
  Name: string;
  Offset: number;
  OffsetMinutes: number;
  Abbreviation?: string;
  AbbreviationDST?: string;
  BeginDST?: string | null;
  EndDST?: string | null;
}

export interface ProductCacheItem {
  ProductID: number;
  Name: string;
  /** Per-module external-reference style field: ArticleNumber on Access/AIM,
   *  Brand on NeoCaddie, etc. Some endpoints serialise it as `string[]` when
   *  a product carries multiple values — readers must handle both. */
  Other?: string | string[];
  Deleted?: boolean;
}

// CommonData nested types (backend: CommonDataCache class)

export interface CommonDataCache {
  Common: unknown[];
  Kiosk: KioskCacheItem[];
  Location: LocationCacheItem[];
  Product: ProductCacheItem[];
  PriceCategory: unknown[];
  PriceType: unknown[];
  CsIssue: unknown[];
  CsIssueType: unknown[];
  IngressGroup?: unknown[];
  IngressMembership?: boolean;
  KioskRevenue?: unknown[];
}

// MovieData nested types (backend: MovieDataCache class)

export interface MovieDataCache {
  Region: unknown[];
  DealType: unknown[];
  Genre: unknown[];
  Sound: unknown[];
  Subtitle: unknown[];
  Language: unknown[];
  Screen: unknown[];
  PersonRole: unknown[];
  MovieCompany: unknown[];
  Persons: unknown[];
  ProductFormats: unknown[];
}

// Permissions, columns, settings

export interface UserPermission {
  RowID: number;
  Read: boolean;
  Write: boolean;
}

export interface ColumnDescription {
  ColumnName: string;
  ColumnFormat: string;
  Header: string;
  DisplayType: string;
  ToolTip: string;
  FilterType: string;
  FilterText: string;
  FilterSettings: string;
  LinkID: number;
  LinkView: string;
  DefaultWidth: number;
  AdminTS?: number;
  LocalTS?: number | null;
}

export interface ServerSetting {
  Key: string;
  Type?: string;
  Value: unknown;
  LocalTS?: number | null;
  AdminTS?: number;
  Local?: boolean;
}

export interface UserStartApp {
  [key: string]: unknown;
}

export interface WebAdminUserExInfo {
  User: string;
  UserID: number;
  UserEmail: string;
  FirstName: string;
  LastName: string;
  UserPhoneNumber: string;
  Address: string;
  City: string;
  ZipCode: string;
  OOID?: number | null;
}

export interface WebPageTS {
  Page: number;
  MenuItem: number;
  Data: number;
  Pivot: number;
  Hierarchy: number;
  PageInfo: number;
}

export interface CommonDataCacheTS {
  LocalCommon: number;
  AdminCommon: number;
  Kiosk: number;
  Location: number;
  PriceType: number;
  PriceCategory: number;
  Product: number;
  CsIssue: number;
  CsIssueType: number;
  KioskRevenue?: string;
}

export interface MovieCacheTS {
  Region: number;
  DealType: number;
  Genre: number;
  Sound: number;
  Subtitle: number;
  Language: number;
  Screen: number;
  PersonRole: number;
  MovieCompany: number;
  Persons: number;
  ProductFormats: number;
}

export interface WebCacheTS {
  PageInfo: WebPageTS;
  CommonData: CommonDataCacheTS;
  MovieData?: MovieCacheTS;
  ColumnInfoAdmin: number;
  ColumnInfoLocal: number;
  Promotions: number;
  TimeZones: number;
  Permissions: number;
  ServerSettingsAdmin: number;
  ServerSettingsLocal: number;
  TicketIssueTypes: number;
  TicketStatuses: number;
  IssueCategories: number;
  ApprovalKioskTests: number;
}

export interface InitCache {
  PageInfo: PageData;
  CommonData: CommonDataCache;
  MovieData?: MovieDataCache;
  ColumnInfo: ColumnDescription[];
  Promotions: unknown[];
  TimeZones: TimeZoneCacheItem[];
  Permissions: UserPermission[];
  ServerSettings: ServerSetting[];
  UserApps: UserStartApp[];
  TicketStatuses: unknown[];
  TicketIssueTypes: unknown[];
  IssueCategories: unknown[];
  OwnerOperatorID?: number | null;
  AvailableOperatorID: number[];
  UserInfo?: WebAdminUserExInfo;
  IpAddress?: string;
  /** Real signed-in user's "SuperUserTools" flag. Grants the restricted super-admin
   *  tool subset (Quick Actions, Impersonate, User File Browser, Serial Lookup).
   *  Authoritative permission enforcement is server-side; this only drives UI visibility. */
  SuperUserTools?: boolean;
  /** Effective super-admin flag the server gates on — follows impersonation (the impersonated
   *  user's flag, or a select-mode override). Absent from caches built by an older server. */
  SuperAdmin?: boolean;
  /** The current session used a second factor at sign-in. Absent from caches built by an older server. */
  SecondFactorUsed?: boolean;
  /** When a second-factor code was last entered for this session (ISO), if known. */
  SecondFactorTime?: string | null;
  /** Whole-hour UTC offset of the database clock. A snapshot taken when the cache
   *  was built, so prefer `ServerTimezoneIana` when it is non-empty. */
  ServerTimezoneOffset: number;
  /** IANA id of the database's zone ("Europe/Stockholm"), or "" when the backend
   *  could not map it. Lets the client resolve the server offset at each value's
   *  own instant instead of reusing the snapshot across a DST boundary. */
  ServerTimezoneIana?: string;
  ApprovalKioskTests: unknown[];
  /** Raw JSON blob of per-database feature flags, supplied by the backend on
   *  endpoint entry. Drives per-database feature toggles (product
   *  rental/reserve/eat-here, etc.) that used to be hard-coded customer/endpoint
   *  lists. Parse with `getDbFeatureFlags`; absent → all-false. */
  DbFeatureFlags?: string;
  /** Brand the server pins the active endpoint to ("maikoz"), "" when it has none of its own.
   *  Absent from servers older than the field; brand.ts's own list decides then. */
  EndpointBrand?: string;
  /** Present only when AdminServerService was built in DEBUG. Gates dev-only UI (e.g. IP impersonation). */
  IsDebugBuild?: boolean;
  TS?: WebCacheTS;
}
